import React, { useState, useRef } from 'react';
import { Box, Paper, Typography, IconButton, Alert, CircularProgress } from '@mui/material';
import { Mic, Stop } from '@mui/icons-material';
import apiClient from '../services/api';
import { formatAmount } from '../utils/formatHelpers';
import goldColors from '../themes/goldColors';
import { useLanguage } from '../contexts/LanguageContext';

// 11 ago 2026: reescrito -- el componente original tenía tres problemas
// que impedían que esto funcionara en producción (ver
// PCM_estado_del_proyecto.md, pedido de Augusto de activar la voz):
//
// 1. Usaba `fetch('/api/v1/voice/upload', ...)` con una URL relativa. En
//    producción el frontend vive en Netlify y el backend en Render --
//    dominios distintos -- así que esa ruta relativa le pegaba a Netlify,
//    no al backend, y devolvía 404. El resto de la app usa `apiClient`
//    (services/api.js) precisamente para evitar esto.
// 2. El `fetch` no mandaba el header Authorization -- aunque la URL
//    hubiera apuntado al backend correcto, /voice/upload exige sesión
//    (get_current_user) y habría devuelto 401.
// 3. Después de subir el audio, intentaba parsear el texto a mano con
//    `.split()` esperando el formato rígido "income 50 coffee" y creaba
//    la transacción de nuevo con `dispatch(addTransaction(...))`. Eso ya
//    no aplica: el backend ahora transcribe Y crea la transacción del
//    lado del servidor (ver endpoints/voice.py) -- volver a crearla acá
//    la hubiera duplicado.
const VoiceInput = () => {
  const { language } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null); // { severity: 'success' | 'error', text }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    setMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Detener los tracks del micrófono -- si no, el navegador deja el
        // ícono de "grabando" activo indefinidamente.
        stream.getTracks().forEach((track) => track.stop());

        // Usar el mimeType real que reportó el MediaRecorder (típicamente
        // audio/webm en Chrome/Edge, audio/mp4 en Safari) en vez de
        // etiquetarlo como 'audio/wav' a mano como hacía la versión
        // anterior -- el archivo no era wav de verdad, solo se llamaba
        // así, lo que puede confundir al servicio de transcripción.
        const mimeType = recorder.mimeType || 'audio/webm';
        const extension = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const audioFile = new File([audioBlob], `voice-command.${extension}`, { type: mimeType });

        setIsProcessing(true);
        try {
          const formData = new FormData();
          formData.append('file', audioFile);

          // No forzar Content-Type: dejar que el navegador arme el
          // multipart/form-data con el boundary correcto -- apiClient trae
          // 'Content-Type: application/json' por defecto para el resto de
          // la app, así que hay que quitarlo puntualmente para esta llamada.
          const response = await apiClient.post('/voice/upload', formData, {
            headers: { 'Content-Type': undefined },
          });

          const data = response.data;
          if (data.success) {
            const tx = data.transaction;
            // Sin símbolo/código de moneda fijo (formatCurrency siempre
            // formatea en USD) -- se antepone el código real (moneda de la
            // transacción, o "" si no vino ninguna) al monto formateado,
            // mismo patrón que el resto de la app (ver formatHelpers.js).
            const monto = tx ? `${tx.moneda ? tx.moneda + ' ' : ''}${formatAmount(tx.monto)}` : '';
            const resumen =
              tx && tx.tipo === 'ingreso'
                ? (language === 'es' ? `Ingreso de ${monto} registrado` : `Income of ${monto} recorded`)
                : (language === 'es' ? `Gasto de ${monto} registrado` : `Expense of ${monto} recorded`);
            // 28 ago 2026 (ver app/services/budget_alerts.py en el
            // backend): si el gasto se acerca o supera el presupuesto
            // mensual de su categoría, se agrega la alerta debajo de la
            // confirmación y se sube la severidad a "warning" para que no
            // pase desapercibida.
            const texto = data.alerta_presupuesto
              ? `"${data.text}" → ${resumen}\n${data.alerta_presupuesto}`
              : `"${data.text}" → ${resumen}`;
            setMessage({ severity: data.alerta_presupuesto ? 'warning' : 'success', text: texto });
          } else {
            setMessage({ severity: 'error', text: data.message || (language === 'es' ? 'No se pudo procesar el audio.' : 'Could not process the audio.') });
          }
        } catch (error) {
          console.error('Error procesando comando de voz:', error);
          const backendMessage = error.response?.data?.detail;
          setMessage({
            severity: 'error',
            text: backendMessage || (language === 'es' ? 'Error al procesar el comando de voz.' : 'Error processing voice command.'),
          });
        } finally {
          setIsProcessing(false);
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accediendo al micrófono:', error);
      setMessage({
        severity: 'error',
        text: language === 'es' ? 'No se pudo acceder al micrófono. Revisa los permisos del navegador.' : 'Could not access the microphone. Check your browser permissions.',
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        {language === 'es' ? 'Entrada de voz' : 'Voice Input'}
      </Typography>
      <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
        {language === 'es'
          ? 'Di algo como: "gasté 50 soles en el mercado" o "recibí 300 dólares de un cliente"'
          : 'Say something like: "I spent 50 dollars on groceries" or "I received 300 dollars from a client"'}
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ width: '100%', whiteSpace: 'pre-line' }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          color="primary"
          size="large"
          disabled={isProcessing}
          onClick={isRecording ? stopRecording : startRecording}
        >
          {isProcessing ? (
            <CircularProgress size={28} sx={{ color: goldColors.main }} />
          ) : isRecording ? (
            <Stop sx={{ color: goldColors.main }} />
          ) : (
            <Mic sx={{ color: goldColors.main }} />
          )}
        </IconButton>
        <Typography variant="body1" color={isRecording ? 'error' : 'primary'}>
          {isProcessing
            ? (language === 'es' ? 'Procesando...' : 'Processing...')
            : isRecording
            ? (language === 'es' ? 'Grabando... clic para detener' : 'Recording... click to stop')
            : (language === 'es' ? 'Clic para grabar' : 'Click to start')}
        </Typography>
      </Box>
    </Paper>
  );
};

export default VoiceInput;
